import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  useCarbonProjectsSelector,
  useFavoriteProjectsSelector,
  useForestationProjectsSelector,
  useRecommendedProjectsSelector,
  useSelectedProjectSelector,
  useSelectedRecommendationTypeSelector,
} from '../../../store/projects/selectors';
import {
  changePaginationAction,
  setSelectedRecommendationTypeAction,
} from '../../../store/projects/actions';
import { RecommendationProjectTypeEnum } from '../../../types';
import {
  CARBON_PROJECT_COLUMNS,
  RECOMMENDATION_PROJECT_COLUMNS,
  FORESTATION_PROJECT_COLUMNS,
  SAVED_PROJECT_COLUMNS,
} from '../components/table/ProjectTableConfig';
import { getProjectNavigationPath } from '../components/table/utils';

/**
 * Smart hook that manages all project table logic and data
 */
export const useProjectTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Selectors
  const selectedProject = useSelectedProjectSelector();
  const selectedRecommendationType = useSelectedRecommendationTypeSelector();

  const carbonData = useCarbonProjectsSelector();
  const recommendedData = useRecommendedProjectsSelector();
  const forestationData = useForestationProjectsSelector();
  const favoriteData = useFavoriteProjectsSelector();

  /**
   * Get the current data based on selected project type
   */
  const getCurrentData = () => {
    switch (selectedProject) {
      case 'current':
        return carbonData.data.items;

      case 'recommendation':
        return selectedRecommendationType === RecommendationProjectTypeEnum.Forests
          ? forestationData.data.items
          : recommendedData.data.items;

      case 'saved':
        return favoriteData.data.items;

      default:
        return [];
    }
  };

  /**
   * Get the appropriate column configuration
   */
  const getCurrentColumns = () => {
    switch (selectedProject) {
      case 'current':
        return CARBON_PROJECT_COLUMNS;

      case 'recommendation':
        return selectedRecommendationType === RecommendationProjectTypeEnum.Forests
          ? FORESTATION_PROJECT_COLUMNS
          : RECOMMENDATION_PROJECT_COLUMNS;

      case 'saved':
        return SAVED_PROJECT_COLUMNS;

      default:
        return [];
    }
  };

  /**
   * Handle row click navigation
   */
  const handleRowClick = (project: any) => {
    const path = getProjectNavigationPath(project, selectedProject, selectedRecommendationType);
    navigate(path);
  };

  /**
   * Handle recommendation type change
   */
  const handleRecommendationTypeChange = (type: RecommendationProjectTypeEnum) => {
    dispatch(setSelectedRecommendationTypeAction(type));
    dispatch(changePaginationAction({ page: 1 }));
  };

  /**
   * Check if data is loading
   */
  const isLoading = () => {
    switch (selectedProject) {
      case 'current':
        return carbonData.isLoading;
      case 'recommendation':
        return selectedRecommendationType === RecommendationProjectTypeEnum.Forests
          ? forestationData.isLoading
          : recommendedData.isLoading;
      case 'saved':
        return favoriteData.isLoading;
      default:
        return false;
    }
  };

  /**
   * Get loading state specific to current selection
   */
  const getLoadingState = () => {
    return isLoading();
  };

  const currentData = getCurrentData();
  const hasData = currentData.length > 0;

  return {
    // Data
    data: currentData,
    columns: getCurrentColumns(),

    // State
    selectedProject,
    selectedRecommendationType,
    loading: getLoadingState(),

    // Actions
    handleRowClick,
    handleRecommendationTypeChange,

    // Derived state
    hasData,
    showRecommendationTabs: selectedProject === 'recommendation',
  };
};
